import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";

import { useContactViewModel } from "../../../../viewmodels/website/useContactViewModel";

interface ContactViewModalProps {
  isOpen: boolean;
  contactId: string;
  onClose: () => void;
}

const ContactViewModal = ({
  isOpen,
  contactId,
  onClose,
}: ContactViewModalProps) => {
  const { getContactById } = useContactViewModel();

  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState<any>(null);

  useEffect(() => {
    if (!isOpen || !contactId) return;

    const load = async () => {
      setLoading(true);

      const result = await getContactById(contactId);

      setContact(result);

      setLoading(false);
    };

    load();
  }, [isOpen, contactId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden">
        {/* Header */}

        <div className="border-b px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Request Details</h2>

            <p className="text-sm text-gray-500 mt-1">
              View request information
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100"
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}

        {loading ? (
          <div className="flex items-center justify-center h-80">
            <Loader2 size={40} className="animate-spin text-primary" />
          </div>
        ) : !contact ? (
          <div className="p-8 text-center text-gray-500">
            Request not found.
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field
                label="First Name"
                value={contact.firstName}
              />

              <Field
                label="Last Name"
                value={contact.lastName}
              />

              <Field
                label="Email"
                value={contact.email}
              />

              <Field
                label="Phone Number"
                value={contact.phoneNumber}
              />

              <Field
                label="Product"
                value={contact.productName}
              />

              <Field
                label="Created On"
                value={
                  contact.createdOn
                    ? new Date(contact.createdOn).toLocaleString()
                    : "-"
                }
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">
                Message
              </label>

              <div className="border rounded-2xl p-4 bg-slate-50 whitespace-pre-wrap min-h-[150px]">
                {contact.message || "-"}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}

        <div className="border-t px-6 py-5 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 border rounded-xl hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

interface FieldProps {
  label: string;
  value?: any;
}

const Field = ({ label, value }: FieldProps) => (
  <div>
    <label className="block text-sm font-semibold mb-2">
      {label}
    </label>

    <div className="border rounded-xl px-4 py-3 bg-slate-50 min-h-[48px] flex items-center">
      {value || "-"}
    </div>
  </div>
);

export default ContactViewModal;